import { useEffect, useRef } from "react";

export const HeroCanvas = ({ className = "" }) => {
  const ref = useRef(null);
  const mouse = useRef({ x: 0.5, y: 0.5 });

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let w = 0;
    let h = 0;
    let raf;
    let t = 20;

    const nodes = Array.from({ length: 16 }, (_, i) => ({
      x: 0.15 + Math.random() * 0.8,
      y: 0.1 + Math.random() * 0.8,
      r: 1.2 + Math.random() * 2.6,
      speed: 0.3 + Math.random() * 0.7,
      phase: Math.random() * Math.PI * 2,
      depth: 0.3 + Math.random() * 0.7,
      gold: i % 4 === 0,
    }));

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = Math.max(1, Math.floor(w * dpr));
      canvas.height = Math.max(1, Math.floor(h * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const onMove = (e) => {
      mouse.current.x = e.clientX / window.innerWidth;
      mouse.current.y = e.clientY / window.innerHeight;
    };
    window.addEventListener("pointermove", onMove, { passive: true });

    const ribbon = (baseY, amp, phase, color, width, depthOffset) => {
      const mx = (mouse.current.x - 0.5) * 40 * depthOffset;
      const my = (mouse.current.y - 0.5) * 26 * depthOffset;
      ctx.beginPath();
      for (let x = -30; x <= w + 30; x += 10) {
        const y =
          h * baseY +
          Math.sin(x * 0.0038 + t * 0.5 + phase) * amp +
          Math.sin(x * 0.0105 + t * 0.27 + phase * 2.2) * amp * 0.38;
        if (x === -30) ctx.moveTo(x + mx, y + my);
        else ctx.lineTo(x + mx, y + my);
      }
      ctx.strokeStyle = color;
      ctx.lineWidth = width;
      ctx.stroke();
    };

    const growthY = (p) => h * 0.88 - Math.pow(p, 1.9) * h * 0.52;

    const draw = () => {
      t += 0.006;
      ctx.clearRect(0, 0, w, h);

      const g = ctx.createRadialGradient(w * 0.62, h * 0.38, 0, w * 0.62, h * 0.38, Math.max(w, h) * 0.62);
      g.addColorStop(0, "rgba(5,150,105,0.12)");
      g.addColorStop(0.6, "rgba(79,179,217,0.05)");
      g.addColorStop(1, "rgba(5,150,105,0)");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);

      ribbon(0.74, h * 0.075, 0, "rgba(5,150,105,0.34)", 1.4, 0.5);
      ribbon(0.62, h * 0.1, 1.7, "rgba(79,179,217,0.30)", 1.1, 0.8);
      ribbon(0.84, h * 0.05, 3.4, "rgba(217,164,32,0.22)", 1, 1);

      const mx = (mouse.current.x - 0.5) * 18;
      const my = (mouse.current.y - 0.5) * 12;

      ctx.beginPath();
      for (let i = 0; i <= 120; i += 1) {
        const p = i / 120;
        const x = w * 0.06 + p * w * 0.88 + mx;
        const y = growthY(p) + Math.sin(p * 7 + t * 1.4) * 3 + my;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.strokeStyle = "rgba(5,150,105,0.6)";
      ctx.lineWidth = 1.6;
      ctx.stroke();

      const pDot = (t * 0.06) % 1;
      const dx = w * 0.06 + pDot * w * 0.88 + mx;
      const dy = growthY(pDot) + Math.sin(pDot * 7 + t * 1.4) * 3 + my;
      const dg = ctx.createRadialGradient(dx, dy, 0, dx, dy, 22);
      dg.addColorStop(0, "rgba(16,185,129,0.85)");
      dg.addColorStop(0.3, "rgba(5,150,105,0.3)");
      dg.addColorStop(1, "rgba(5,150,105,0)");
      ctx.fillStyle = dg;
      ctx.beginPath();
      ctx.arc(dx, dy, 22, 0, Math.PI * 2);
      ctx.fill();

      ctx.lineWidth = 1;
      ctx.strokeStyle = "rgba(16,32,46,0.12)";
      ctx.beginPath();
      ctx.arc(w * 0.72 + mx * 1.4, h * 0.3 + my * 1.4 + Math.sin(t * 0.8) * 8, Math.min(w, h) * 0.16, 0, Math.PI * 2);
      ctx.stroke();
      ctx.strokeStyle = "rgba(5,150,105,0.22)";
      ctx.beginPath();
      ctx.arc(w * 0.3 - mx, h * 0.62 - my + Math.cos(t * 0.7) * 10, Math.min(w, h) * 0.1, 0, Math.PI * 2);
      ctx.stroke();

      nodes.forEach((n) => {
        const nx = n.x * w + (mouse.current.x - 0.5) * 30 * n.depth + Math.sin(t * n.speed + n.phase) * 6;
        const ny = n.y * h + (mouse.current.y - 0.5) * 20 * n.depth + Math.cos(t * n.speed + n.phase) * 8;
        const alpha = 0.35 + Math.sin(t * 1.6 + n.phase) * 0.25;
        ctx.fillStyle = n.gold ? `rgba(245,158,11,${alpha})` : `rgba(52,211,153,${alpha})`;
        ctx.beginPath();
        ctx.arc(nx, ny, n.r, 0, Math.PI * 2);
        ctx.fill();
      });

      if (!reduce) raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("pointermove", onMove);
    };
  }, []);

  return <canvas ref={ref} data-testid="hero-canvas" className={className} aria-hidden="true" />;
};
